import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { isEqual } from 'lodash-es';

import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, ReplaySubject } from 'rxjs';
import { debounceTime, delay, skip, take, takeUntil } from 'rxjs/operators';

import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatButton } from '@angular/material/button';
import {
  AvailableLanguages,
  isOtherLegalBasis,
  isSchengenCountryIssuedDocumentLegalBasis,
  isVisaFreeLegalBasis,
  isVisaLegalBasis,
  patterns,
  shouldShowEntryDocumentFields,
} from '@constants';
import { DeviceInfoService } from '@core/services/device-info.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import {
  AllDeviceInfo,
  GUIDE_NAME,
  GuideFinishedOpts,
  PersonalDetails,
  PROCESS_CATEGORY,
  UserProcess,
} from '@interfaces';
import { LayoutService } from '@layout/layout.service';
import {
  customDateValidator,
  forbiddenDateInPast,
  forbiddenFutureDate,
  isBooleanValidator,
  isNumericValidator,
} from '@shared/custom-validators';
import { IntroModalService, MODAL_MESSAGE } from '@shared/intro-modal/intro-modal.service';
import { ModeChangedModalService } from '@shared/mode-changed-modal/mode-changed-modal.service';
import { focusPersonalDataStepControl, scrollPersonalDataStepIntoView } from '../personal-data-stepper-scroll';

type DateFormats = {
  [key: string]: string;
};

type FormStep = 'step1' | 'step2' | 'step3' | 'step4' | 'step5';

@Component({
  selector: 'app-temp-premit-form',
  templateUrl: './temp-premit-form.component.html',
  styleUrls: ['./temp-premit-form.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  standalone: false,
})
export class TempPremitFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly layoutService = inject(LayoutService);
  private readonly introModal = inject(IntroModalService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly modeChangedModal = inject(ModeChangedModalService);
  private readonly deviceInfoService = inject(DeviceInfoService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @ViewChild('mainStepper') mainStepper: MatStepper;
  @ViewChild('stepperContainer') stepperContainer: ElementRef;
  @ViewChild('saveAndProceedButton') saveAndProceedButton: MatButton;

  @Input() userProcess: UserProcess;
  @Input() currentLang: AvailableLanguages;
  @Input() loadingUserProcess: boolean;
  @Input() errorMessage: string;
  @Input() mode: 'foreigner' | 'employee';

  @Input() correctlyFilledSteps: number;
  @Input() personalDataForm: FormGroup<{ [key in FormStep]: FormGroup }>;
  @Input() bottomActionBarInFlow: boolean = false;
  @Input() percentageOfCompletness: number;
  @Input() totalStepsInForm: number;
  @Input() dateFormatsForFields: DateFormats;

  @Output() updateDetails = new EventEmitter<PersonalDetails>();
  @Output() confirmDetails = new EventEmitter<void>();
  @Output() confirmVoivoChange = new EventEmitter<void>();
  @Output() toggleVerificationOfPersonalDetails = new EventEmitter();

  public stepper: MatStepper;
  public GUIDE_NAME = GUIDE_NAME;
  public deviceInfo: AllDeviceInfo;

  ngOnInit(): void {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
    });
  }

  public async ngAfterViewInit(): Promise<void> {
    this.mainStepper?.selectionChange.pipe(takeUntil(this.destroy$)).subscribe(({ previouslySelectedIndex }) => {
      const subFormKey = `step${previouslySelectedIndex + 1}` as FormStep;
      this.personalDataForm.get(subFormKey).markAllAsTouched();
    });

    if (this.personalDataForm) {
      setTimeout(() => {
        this.addStep3ValidationChanges(this.personalDataForm.controls['step3'].controls);
        this.addStep4ValidationChanges(this.personalDataForm.controls['step4'].controls);
        this.registerValueChangesHandler();
      });
    }

    if (this.mode !== 'foreigner') {
      return;
    }
    const wasLoggedInBefore = this.localStorage.getFirstLogin();
    const hasSeenPersonalDataForm = this.localStorage.getSeenPersonalDataForm(this.userProcess.id);
    const hasSeenPersonalDataFormWithoutProcess = this.localStorage.getSeenPersonalDataForm();

    if (!wasLoggedInBefore) {
      await lastValueFrom(
        this.introModal
          .open({ messageKey: MODAL_MESSAGE.USER_WELCOME, showActionButtons: false })
          .afterClosed()
          .pipe(take(1))
      );
    }

    if (!wasLoggedInBefore && this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      await lastValueFrom(this.layoutService.startGuide(GUIDE_NAME.FIRST_TIME, this.translateService).pipe(take(1)));
    }

    if (!hasSeenPersonalDataForm && !hasSeenPersonalDataFormWithoutProcess) {
      await lastValueFrom(
        this.introModal
          .open({ messageKey: MODAL_MESSAGE.PERSONAL_DETAILS_INSTRUCTIONS, showActionButtons: false })
          .afterClosed()
          .pipe(take(1))
      );
    }

    if (
      !hasSeenPersonalDataForm &&
      !hasSeenPersonalDataFormWithoutProcess &&
      this.deviceInfo.deviceTypeDetected === 'DESKTOP'
    ) {
      await this.startPersonalDataFormGuide(GUIDE_NAME.PERSONAL_DATA_FORM, 1);
    }

    this.localStorage.setFirstLogin();
    this.localStorage.setSeenPersonalDataForm(this.userProcess.id, true);

    this.modeChangedModal.openIfPostModeAndNotSeen({ userProcess: this.userProcess });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public async initGuide(name: GUIDE_NAME, guideOfStep: number): Promise<void> {
    if (this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      await lastValueFrom(
        this.introModal
          .open({ messageKey: MODAL_MESSAGE.PERSONAL_DETAILS_INSTRUCTIONS, showActionButtons: false })
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroy$))
      );
      this.startPersonalDataFormGuide(name, guideOfStep, PROCESS_CATEGORY.TEMP_PREMIT);
    }
  }

  public async startPersonalDataFormGuide(
    name: GUIDE_NAME,
    guideOfStep: number,
    processCategory?: PROCESS_CATEGORY
  ): Promise<GuideFinishedOpts> {
    if (this.mainStepper.selectedIndex + 1 !== guideOfStep) {
      await lastValueFrom(this.mainStepper.animationDone.pipe(take(1), takeUntil(this.destroy$)));
    }

    const guideFinishedOpts = await lastValueFrom(
      this.layoutService
        .startGuide(name, this.translateService, processCategory)
        .pipe(take(1), takeUntil(this.destroy$))
    );

    this.localStorage.setPersonalDetailsFormStepSeen(guideOfStep, true);
    return guideFinishedOpts;
  }

  public stepAnimationDone(): void {
    const { selectedIndex } = this.mainStepper;
    const indexToId = [
      'applicantName',
      'applicantMaritalStatus',
      'meansOfSubsistence-0-input',
      'inPolandAtApplicationSubmition-0-input',
      'applicantNoFamilyMembersInPoland-0-input',
    ];

    const firstElemId = indexToId[selectedIndex];

    if (!firstElemId || (this.mode !== 'foreigner' && selectedIndex === 0)) {
      return;
    }

    scrollPersonalDataStepIntoView({
      stepper: this.mainStepper,
      stepperContainer: this.stepperContainer,
      selectedIndex,
      firstControlId: firstElemId,
      isDesktop: this.deviceInfo.deviceTypeDetected === 'DESKTOP',
    });

    if (this.deviceInfo.deviceTypeDetected !== 'DESKTOP') {
      return;
    }

    // Do not focus any INPUT if this is EMPLOYEE
    if (this.mode !== 'foreigner') {
      return;
    }

    setTimeout(() => {
      const firstElem = focusPersonalDataStepControl(this.stepperContainer, firstElemId);

      if (!firstElem) {
        return;
      }

      if (firstElemId.includes('-')) {
        firstElem.classList.add('cdk-keyboard-focused');
      }
    }, 150);
  }

  // Validating all of user inputs and setting stepState of all steps
  public saveAndProceed(): void {
    this.personalDataForm.markAllAsTouched();
    this.personalDataForm.markAsDirty();
    this.confirmDetails.emit();
  }

  private registerValueChangesHandler(): void {
    this.personalDataForm.valueChanges.pipe(skip(1), debounceTime(600), takeUntil(this.destroy$)).subscribe(changes => {
      const dataToSend = {} as any;
      const stepsGroups = { ...changes };
      Object.values(stepsGroups).forEach(group => {
        Object.entries(group).forEach(([fieldName, fieldValue]: [string, any]) => {
          const dateFormat: string | undefined = this.dateFormatsForFields[fieldName];

          if (fieldValue?._isAMomentObject && dateFormat) {
            dataToSend[fieldName] = fieldValue.format(dateFormat);
          } else {
            dataToSend[fieldName] = fieldValue;
          }

          if (fieldName === 'descriptionHeight' && fieldValue) {
            dataToSend['descriptionHeight'] = Math.floor(fieldValue);
          }
        });
      });

      this.correctlyFilledSteps = 0;
      Object.values(this.personalDataForm.controls).forEach(formGroup => {
        if (formGroup.valid) {
          this.correctlyFilledSteps += 1;
        }
      });
      this.percentageOfCompletness = Math.ceil((this.correctlyFilledSteps / this.totalStepsInForm) * 100);
      setTimeout(() => this.layoutService.progressPercentage$.next(this.percentageOfCompletness));

      const personalDetails = this.userProcess.personalDetails;
      const differences = [] as any;
      Object.keys(this.userProcess.personalDetails).forEach((udk: any) => {
        const key = udk as keyof PersonalDetails;
        const original = personalDetails[key];
        const changed = dataToSend[key];

        if (dataToSend[key] !== undefined && !isEqual(original, changed)) {
          differences.push({
            key,
            original,
            changed,
          });
        }
      });

      if (!differences.length) {
        return;
      }

      this.updateDetails.emit(dataToSend);
    });
  }

  private addStep3ValidationChanges(controls: { [key: string]: AbstractControl }): void {
    const meansOfSubsistenceValue = controls.meansOfSubsistence.value;
    this.toggleMeansOfSubsistence(meansOfSubsistenceValue);

    controls.meansOfSubsistence.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(meansOfSubsistenceValue => {
      this.toggleMeansOfSubsistence(meansOfSubsistenceValue);
    });
  }

  private addStep4ValidationChanges(controls: { [key: string]: AbstractControl }): void {
    const inPolandAtApplicationSubmition = controls.inPolandAtApplicationSubmition.value;
    this.toggleRequiredOfCurrentlyLivingInPolandAndBasisOfStay(inPolandAtApplicationSubmition);

    const shouldPreviousVisitsBeRequired = controls.noPreviousVisits.value === false;
    this.toggleRequiredOfPreviousVisits(shouldPreviousVisitsBeRequired);

    const noLastTravelsOutsidePolandValue = controls.noLastTravelsOutsidePoland.value;
    this.toggleRequiredOfLastTravelsOutsidePoland(noLastTravelsOutsidePolandValue);

    const legalBasisOfStayValue = controls.legalBasisOfStay.value;
    this.toggleLegalBasisOfStayComment(legalBasisOfStayValue);
    this.toggleRequiredOfLegalBasisOfStay(legalBasisOfStayValue);

    const currentlyLivingInPolandValue = controls.currentlyLivingInPoland.value;
    this.toggleCurrentlyLivingInPoland(currentlyLivingInPolandValue);

    // const lastEntryDate = controls.lastEntryDate.value;
    // Only when lastEntryDate set to some date, set noPreviousVisits to false because:
    // User entered PL atleast once so he must provide previousVisits info
    // When lastEntryDate set to null/false, do nothing with noPreviousVistis field
    // if (!!lastEntryDate) {
    // noPreviousVisits = false - double negation - user HAD PREVIOUS VISITS
    // controls.noPreviousVisits.setValue(false);
    // }
    // this.toggleRequiredOfPreviousVisits(!lastEntryDate);

    controls.inPolandAtApplicationSubmition.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(inPLAtSubmition => {
        this.toggleRequiredOfCurrentlyLivingInPolandAndBasisOfStay(inPLAtSubmition);
      });

    controls.currentlyLivingInPoland.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearBasisOfStay();
    });

    controls.noLastTravelsOutsidePoland.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(noLastTravels => {
        this.toggleRequiredOfLastTravelsOutsidePoland(noLastTravels);
      });

    controls.noPreviousVisits.valueChanges.pipe(delay(50), takeUntil(this.destroy$)).subscribe(noPreviousVisits => {
      const previousVisitsRequired = noPreviousVisits === false;
      this.toggleRequiredOfPreviousVisits(previousVisitsRequired);
    });

    controls.legalBasisOfStay.valueChanges.pipe(delay(50), takeUntil(this.destroy$)).subscribe(legalBasisOfStay => {
      this.toggleLegalBasisOfStayComment(legalBasisOfStay);
      this.toggleRequiredOfLegalBasisOfStay(legalBasisOfStay);
    });

    controls.currentlyLivingInPoland.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(currentlyLivingInPolandValue => {
        this.toggleCurrentlyLivingInPoland(currentlyLivingInPolandValue);
      });

    // We don't care about last entry date now
    // controls.lastEntryDate.valueChanges.pipe(
    //   delay(50), takeUntil(this.destroy$),
    // ).subscribe(entryDate => {
    // if (!!entryDate) {
    //   controls.noPreviousVisits.setValue(false);
    // }
    // });
  }

  private toggleMeansOfSubsistence(meansOfSubsistence: string): void {
    const step3Controls = this.personalDataForm.controls['step3'].controls;
    const meansOfSubsistenceLc = meansOfSubsistence?.toLowerCase();

    if (meansOfSubsistenceLc === 'oszczędności') {
      step3Controls.meansOfSubsistenceSavingsAmount.setValidators([Validators.required, Validators.maxLength(200)]);
    }

    if (meansOfSubsistenceLc !== 'oszczędności') {
      step3Controls.meansOfSubsistenceSavingsAmount.setValidators([Validators.maxLength(200)]);
      step3Controls.meansOfSubsistenceSavingsAmount.reset(null);
    }

    if (meansOfSubsistenceLc === 'inne') {
      step3Controls.meansOfSubsistenceOthers.setValidators([Validators.required, Validators.maxLength(200)]);
    }

    if (meansOfSubsistenceLc !== 'inne') {
      step3Controls.meansOfSubsistenceOthers.setValidators([Validators.maxLength(200)]);
      step3Controls.meansOfSubsistenceOthers.reset(null);
    }

    step3Controls.meansOfSubsistenceSavingsAmount.updateValueAndValidity();
    step3Controls.meansOfSubsistenceOthers.updateValueAndValidity();
  }

  private toggleRequiredOfCurrentlyLivingInPolandAndBasisOfStay(inPolandAtApplicationSubmition: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;
    if (!inPolandAtApplicationSubmition) {
      step4Controls.currentlyLivingInPoland.setValidators([isBooleanValidator]);
      step4Controls.currentlyLivingInPoland.reset(null);

      step4Controls.legalBasisOfStay.setValidators([Validators.maxLength(1000)]);
      step4Controls.legalBasisOfStay.reset(null);

      step4Controls.residenceVisaExpiryDate.setValidators([customDateValidator]);
      step4Controls.residenceVisaExpiryDate.reset(null);

      step4Controls.lastEntryDate.setValidators([customDateValidator]);
      step4Controls.lastEntryDate.reset(null);
    }

    if (inPolandAtApplicationSubmition) {
      step4Controls.currentlyLivingInPoland.setValidators([Validators.required, isBooleanValidator]);

      step4Controls.legalBasisOfStay.setValidators([Validators.required, Validators.maxLength(1000)]);
      step4Controls.residenceVisaExpiryDate.setValidators([Validators.required, customDateValidator]);
      step4Controls.lastEntryDate.setValidators([Validators.required, customDateValidator]);
    }

    step4Controls.currentlyLivingInPoland.updateValueAndValidity();

    step4Controls.legalBasisOfStay.updateValueAndValidity();
    step4Controls.residenceVisaExpiryDate.updateValueAndValidity();
    step4Controls.lastEntryDate.updateValueAndValidity();

    step4Controls.previousVisitsArr.updateValueAndValidity();
  }

  private clearBasisOfStay(): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    step4Controls.legalBasisOfStay.reset(null);
    step4Controls.residenceVisaExpiryDate.reset(null);
    step4Controls.lastEntryDate.reset(null);
    step4Controls.legalBasisOfStayComment.reset(null);
    step4Controls.stayVisaType.reset(null);
    step4Controls.stayVisaSeries.reset(null);
    step4Controls.stayVisaNumber.reset(null);
    step4Controls.stayVisaIssueDate.reset(null);
    step4Controls.stayVisaExpiryDate.reset(null);
    step4Controls.stayVisaPeriodOfStay.reset(null);
    step4Controls.stayVisaIssuingAuthority.reset(null);
    step4Controls.stayEntitlingDocumentIssuedBy.reset(null);
    step4Controls.stayEntitlingDocumentIssueDate.reset(null);
    step4Controls.entryDocumentSeries.reset(null);
    step4Controls.entryDocumentNumber.reset(null);
    step4Controls.entryDocumentIssueDate.reset(null);
    step4Controls.entryDocumentExpiryDate.reset(null);
    step4Controls.entryDocumentIssuingAuthority.reset(null);
  }

  private toggleRequiredOfPreviousVisits(shouldBeRequired: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;
    if (shouldBeRequired) {
      step4Controls.previousVisitsArr.setValidators([
        Validators.required,
        // Validators.maxLength(1000),
        // Validators.pattern(patterns.PLTextValidator),
      ]);
    }

    if (!shouldBeRequired) {
      step4Controls.previousVisitsArr.setValidators([
        // Validators.maxLength(1000),
        // Validators.pattern(patterns.PLTextValidator),
      ]);
    }

    step4Controls.previousVisitsArr.updateValueAndValidity();
  }

  private toggleRequiredOfLastTravelsOutsidePoland(noLastTravels: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;
    // if noLastTravels === false then user HAS TO PROVIDE LAST TRAVELS
    if (!noLastTravels) {
      step4Controls.lastTravelsOutsidePoland.setValidators([
        Validators.required,
        Validators.maxLength(5000),
        // Validators.pattern(patterns.PLTextValidator),
      ]);
    }

    // if noLastTravels === true then user should not provide any travels
    if (noLastTravels) {
      step4Controls.lastTravelsOutsidePoland.setValidators([
        Validators.maxLength(5000),
        // Validators.pattern(patterns.PLTextValidator),
      ]);
    }

    step4Controls.lastTravelsOutsidePoland.updateValueAndValidity();
  }

  private toggleLegalBasisOfStayComment(legalBasisOfStayValue: string): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (!isOtherLegalBasis(legalBasisOfStayValue)) {
      step4Controls.legalBasisOfStayComment.setValidators([
        // Validators.pattern(patterns.PLTextValidator)
        Validators.maxLength(1000),
      ]);
    }

    if (isOtherLegalBasis(legalBasisOfStayValue)) {
      step4Controls.legalBasisOfStayComment.setValidators([
        Validators.required,
        Validators.maxLength(1000),
        // Validators.pattern(patterns.PLTextValidator)
      ]);
    }

    step4Controls.legalBasisOfStayComment.updateValueAndValidity();
  }

  private toggleRequiredOfLegalBasisOfStay(legalBasisOfStay: string): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;
    const isVisa = isVisaLegalBasis(legalBasisOfStay);
    const isSchengenCountryIssuedDoc = isSchengenCountryIssuedDocumentLegalBasis(legalBasisOfStay);
    const isVisaFree = isVisaFreeLegalBasis(legalBasisOfStay);
    const shouldShowEntryDocument = shouldShowEntryDocumentFields(legalBasisOfStay);

    if (isVisa) {
      step4Controls.stayVisaType.setValidators([Validators.required, Validators.maxLength(100)]);
      step4Controls.stayVisaSeries.setValidators([
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(patterns.PLTextValidator),
      ]);
      step4Controls.stayVisaNumber.setValidators([
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(patterns.PLTextValidator),
      ]);
      step4Controls.stayVisaIssueDate.setValidators([Validators.required, customDateValidator]);
      step4Controls.stayVisaExpiryDate.setValidators([Validators.required, customDateValidator]);
      step4Controls.stayVisaPeriodOfStay.setValidators([Validators.required, Validators.min(1), Validators.max(9999)]);
      step4Controls.stayVisaIssuingAuthority.setValidators([Validators.required, Validators.maxLength(100)]);
    }

    if (!isVisa) {
      step4Controls.stayVisaType.reset(null);
      step4Controls.stayVisaSeries.reset(null);
      step4Controls.stayVisaNumber.reset(null);
      step4Controls.stayVisaIssueDate.reset(null);
      step4Controls.stayVisaExpiryDate.reset(null);
      step4Controls.stayVisaPeriodOfStay.reset(null);
      step4Controls.stayVisaIssuingAuthority.reset(null);

      step4Controls.stayVisaType.setValidators([]);
      step4Controls.stayVisaSeries.setValidators([]);
      step4Controls.stayVisaNumber.setValidators([]);
      step4Controls.stayVisaIssueDate.setValidators([]);
      step4Controls.stayVisaExpiryDate.setValidators([]);
      step4Controls.stayVisaPeriodOfStay.setValidators([]);
      step4Controls.stayVisaIssuingAuthority.setValidators([]);
    }

    if (isSchengenCountryIssuedDoc) {
      step4Controls.stayEntitlingDocumentIssuedBy.setValidators([Validators.required, Validators.maxLength(100)]);
      step4Controls.stayEntitlingDocumentIssueDate.setValidators([Validators.required, customDateValidator]);
    } else {
      step4Controls.stayEntitlingDocumentIssuedBy.reset(null);
      step4Controls.stayEntitlingDocumentIssueDate.reset(null);
      step4Controls.stayEntitlingDocumentIssuedBy.setValidators([]);
      step4Controls.stayEntitlingDocumentIssueDate.setValidators([]);
    }

    if (shouldShowEntryDocument) {
      step4Controls.entryDocumentSeries.setValidators([
        Validators.maxLength(100),
        Validators.pattern(patterns.PLTextValidator),
      ]);
      step4Controls.entryDocumentNumber.setValidators([
        Validators.maxLength(100),
        Validators.pattern(patterns.PLTextValidator),
      ]);
      step4Controls.entryDocumentIssueDate.setValidators([customDateValidator]);
      step4Controls.entryDocumentExpiryDate.setValidators([customDateValidator]);
      step4Controls.entryDocumentIssuingAuthority.setValidators([Validators.maxLength(100)]);
    }

    if (!shouldShowEntryDocument || isVisa || isVisaFree) {
      step4Controls.entryDocumentSeries.reset(null);
      step4Controls.entryDocumentNumber.reset(null);
      step4Controls.entryDocumentIssueDate.reset(null);
      step4Controls.entryDocumentExpiryDate.reset(null);
      step4Controls.entryDocumentIssuingAuthority.reset(null);

      step4Controls.entryDocumentSeries.setValidators([]);
      step4Controls.entryDocumentNumber.setValidators([]);
      step4Controls.entryDocumentIssueDate.setValidators([]);
      step4Controls.entryDocumentExpiryDate.setValidators([]);
      step4Controls.entryDocumentIssuingAuthority.setValidators([]);
    }

    [
      step4Controls.stayVisaType,
      step4Controls.stayVisaSeries,
      step4Controls.stayVisaNumber,
      step4Controls.stayVisaIssueDate,
      step4Controls.stayVisaExpiryDate,
      step4Controls.stayVisaPeriodOfStay,
      step4Controls.stayVisaIssuingAuthority,
      step4Controls.stayEntitlingDocumentIssuedBy,
      step4Controls.stayEntitlingDocumentIssueDate,
      step4Controls.entryDocumentSeries,
      step4Controls.entryDocumentNumber,
      step4Controls.entryDocumentIssueDate,
      step4Controls.entryDocumentExpiryDate,
      step4Controls.entryDocumentIssuingAuthority,
    ].forEach(control => {
      control.markAsUntouched();
      control.updateValueAndValidity();
    });
  }

  private toggleCurrentlyLivingInPoland(currentlyLivingInPoland: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (currentlyLivingInPoland === true) {
      step4Controls.lastEntryDate.setValidators([Validators.required, customDateValidator, forbiddenFutureDate()]);
    }

    if (currentlyLivingInPoland === false) {
      step4Controls.lastEntryDate.setValidators([Validators.required, customDateValidator, forbiddenDateInPast()]);
    }

    step4Controls.lastEntryDate.updateValueAndValidity();
  }

  protected readonly PROCESS_CATEGORY = PROCESS_CATEGORY;
}
